import { Checkbox, Tooltip as ChakraTooltip } from "@chakra-ui/react";
import { cn } from "utils/helpers";
import { scopeTypeStyle } from "newui/features/RolesPermissions/utils/scopeTypeStyles";
import type {
	ScopeType,
	UserRoleAssignment,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

interface ManageRolesTableProps {
	assignments: UserRoleAssignment[];
	/** Assignment ids currently checked — owned by the parent modal. */
	selectedIds: number[];
	onToggleRow: (assignmentId: number) => void;
	onToggleAll: (selected: boolean) => void;
}

interface ManageRolesItemCellProps {
	assignment: UserRoleAssignment;
}

interface AccessLevelBadgeProps {
	scopeType: ScopeType;
	/**
	 * Optional because the SAME badge is rendered twice for one assignment (table
	 * row + delete-confirmation list) and a duplicated `data-testid` makes
	 * `getByTestId` ambiguous. Only the table passes the frozen
	 * `access-level-badge-{assignmentId}` id (CONTRACT §8).
	 */
	testId?: string;
}

/**
 * The scope-type pill — first real consumer of `SCOPE_TYPE_STYLE`. Colour and
 * copy come from that shared map (`scopeTypeStyle()` adds the unknown-type
 * fallback), never from a switch here: one place decides that `project_type`
 * reads "Portfolio" and is emerald.
 */
export const AccessLevelBadge = ({ scopeType, testId }: AccessLevelBadgeProps) => {
	/**
	 * PRO-757 A8 — icon AND its extra class come from the SAME shared entry as the colour and
	 * the label. This file used to keep a private `SCOPE_TYPE_ICON` map beside it, and the
	 * author's RFx correction (feather "send", the paper plane, rotated) only ever reached the
	 * shared map: one RFx assignment rendered as a paper plane in the scope picker and as a
	 * file icon in this table, inside the same modal stack. `scopeTypeStyle()` keeps the
	 * original "a new backend scope type needs no change here" property via its own
	 * unknown-type fallback, so nothing is lost by dropping the second map.
	 */
	const style = scopeTypeStyle(scopeType);
	const Icon = style.icon;

	return (
		<span
			className={cn(
				"inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
				style.chip
			)}
			data-testid={testId}
		>
			<Icon size={12} className={style.iconClassName} aria-hidden="true" />
			{style.label}
		</span>
	);
};

/**
 * How much of an item name this column shows before cutting it.
 *
 * Author review 1.1: the column was inheriting `ScopeLabel`'s shared 20-character
 * default, which is sized for a role pill and far too short for a full-width
 * table cell — project and RFx names were unreadable. 50 was given as the FLOOR,
 * and this column can do better than the floor: the Item cell in this `xxl`
 * (1200px) modal has roughly 600px to work with, which is ~80 characters at
 * `text-sm`.
 *
 * Truncation is done here rather than with CSS `text-overflow` deliberately.
 * This table is `table-layout: auto`, so ellipsis truncation would need either a
 * fixed layout (re-specifying every column's width) or a `max-width` guess in
 * `ch` — the same guess as a character count, but one that leaves the component
 * unable to answer "was this actually cut?", which is exactly what decides
 * whether a row gets a tooltip. A JS cut makes that exact and testable.
 */
export const ITEM_MAX_CHARS = 80;

const ELLIPSIS = "…";

export const truncateItemLabel = (label: string): string =>
	label.length > ITEM_MAX_CHARS ? `${label.slice(0, ITEM_MAX_CHARS)}${ELLIPSIS}` : label;

/**
 * Item text for one assignment.
 *
 * A `global` row has no item, and must NOT repeat "Company Wide" — that word is
 * already in the Access Level badge on the same row. It renders an em dash with
 * the spoken equivalent instead (spec-review F5, wording per CONTRACT §10).
 *
 * A name too long to show is cut and carries the full text in a tooltip. The
 * tooltip is Chakra's (the convention this feature already uses — see
 * `PermissionMatrixEditor`) with `shouldWrapChildren`, which makes the trigger
 * focusable and wires `aria-describedby`: a hover-only tooltip would put the
 * missing half of a project name out of reach of keyboard and screen-reader
 * users. A name that fits gets no tooltip — repeating text already on screen is
 * noise.
 */
export const ManageRolesItemCell = ({ assignment }: ManageRolesItemCellProps) => {
	if (assignment.scope_type === "global") {
		return (
			<span role="img" aria-label="No specific item — company wide access">
				—
			</span>
		);
	}

	const label = assignment.scope_label ?? "";

	if (label.length === 0) {
		return <span className="text-gray-500">—</span>;
	}

	const shortened = truncateItemLabel(label);

	if (shortened === label) {
		return <span className="text-gray-700">{label}</span>;
	}

	return (
		<ChakraTooltip label={label} hasArrow openDelay={200} shouldWrapChildren>
			<span className="text-gray-700">{shortened}</span>
		</ChakraTooltip>
	);
};

/**
 * PRO-757 (D7-T1) — the Manage Roles table: one row per CURRENT assignment,
 * columns Role | Access Level | Item, with a checkbox per row for the bulk
 * delete flow.
 *
 * Every row is selectable. `already_granted` (rule 4) restricts selection inside
 * the ADD flow's scope picker only — it never blocks REMOVING a row the user
 * already holds, which is the whole point of this table.
 */
export const ManageRolesTable = ({
	assignments,
	selectedIds,
	onToggleRow,
	onToggleAll,
}: ManageRolesTableProps) => {
	const isEmpty = assignments.length === 0;
	const selectedCount = assignments.filter((assignment) =>
		selectedIds.includes(assignment.assignment_id)
	).length;
	const allSelected = !isEmpty && selectedCount === assignments.length;
	const someSelected = selectedCount > 0 && !allSelected;

	return (
		<div className="overflow-x-auto rounded-md border border-gray-200">
			<table className="w-full border-collapse text-sm" data-testid="manage-roles-table">
				<thead>
					<tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
						<th scope="col" className="w-10 px-3 py-2">
							<Checkbox
								isChecked={allSelected}
								isIndeterminate={someSelected}
								isDisabled={isEmpty}
								onChange={(event) => onToggleAll(event.target.checked)}
								aria-label="Select all roles"
								data-testid="manage-roles-select-all"
							/>
						</th>
						<th scope="col" className="px-3 py-2 font-semibold">
							Role
						</th>
						<th scope="col" className="px-3 py-2 font-semibold">
							Access Level
						</th>
						<th scope="col" className="px-3 py-2 font-semibold">
							Item
						</th>
					</tr>
				</thead>
				<tbody>
					{isEmpty ? (
						<tr>
							<td
								colSpan={4}
								className="px-3 py-8 text-center text-gray-500"
								data-testid="manage-roles-empty"
							>
								No roles assigned yet.
							</td>
						</tr>
					) : (
						assignments.map((assignment) => {
							const checked = selectedIds.includes(assignment.assignment_id);

							return (
								<tr
									key={assignment.assignment_id}
									className={cn(
										"border-t border-gray-100",
										checked ? "bg-blue-50" : "hover:bg-gray-50"
									)}
									data-testid={`manage-roles-row-${assignment.assignment_id}`}
								>
									<td className="px-3 py-2">
										<Checkbox
											isChecked={checked}
											onChange={() => onToggleRow(assignment.assignment_id)}
											aria-label={`Select ${assignment.role_name} — ${
												scopeTypeStyle(assignment.scope_type).label
											}${assignment.scope_label ? `: ${assignment.scope_label}` : ""}`}
											data-testid={`manage-roles-row-checkbox-${assignment.assignment_id}`}
										/>
									</td>
									<td className="px-3 py-2 font-medium text-gray-800">
										{assignment.role_name}
									</td>
									<td className="px-3 py-2">
										<AccessLevelBadge
											scopeType={assignment.scope_type}
											testId={`access-level-badge-${assignment.assignment_id}`}
										/>
									</td>
									<td
										className="px-3 py-2 text-gray-700"
										data-testid={`item-cell-${assignment.assignment_id}`}
									>
										<ManageRolesItemCell assignment={assignment} />
									</td>
								</tr>
							);
						})
					)}
				</tbody>
			</table>
		</div>
	);
};
